import { Fragment, useState, useEffect } from 'react';
import MenuItem from '../../../components/MenuItem';
import { bundleDisplayIdxComparer, isBundleInStock } from '../../../helpers/bundleHelpers';
import { ItemMenuHeader } from '../../../components/ItemMenuHeader';
import PromotionCardItem from '../../../components/PromotionCardItem';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { useGetBannerConfigQuery } from '../../../services/customer.api'
import { useAppSelector } from '../../../store/storeTypes';

const ItemMenu = ({
  categoryRefs,
  subCategoryHeaderRefs,
  subCategoryRefs,
  menuCategories,
  basket,
  navigateTo,
  addItem,
  removeItem,
  findItemQuantity,
  setQuickAddModalState,
  quickAddModalState,
  setMakeItAMealModalState,
  makeItAMealModalState,
  makeItAMealBundles,
  makeItAMealBundleOptions,
  makeItAMealVariant,
  makeItAMealModalTimesShown,
  refs,
  showMenuHeaderAtTopOfPage,
  inApp,
  onOfferSelected
}) => {
  const isSmallScreen = useSelector(state => state.session.isSmallScreen);
  const hasPromotionalCard = useRef(true);
  const { data } = useGetBannerConfigQuery();
  const [displayCategories, setDisplayCategories] = useState(menuCategories);
  const menuOffers = useAppSelector(state => state.menu.offers);

  useEffect(() => {
    setDisplayCategories(menuCategories);
    const firstItem = menuCategories
      ?.flatMap(mc => mc.subCategories ?? [])
      ?.flatMap(sc => [...(sc.bundlesToRender ?? []), ...(sc.itemsToRender ?? [])])
      ?.find(item => item.imageUrl);
    if (!firstItem) return;
    const img = new Image();
    img.src = firstItem.imageUrl;
    img.onerror = () => setDisplayCategories(
      menuCategories?.map(mc => ({
        ...mc,
        subCategories: mc.subCategories?.map(sc => ({
          ...sc,
          bundlesToRender: sc.bundlesToRender?.map(item => ({ ...item, imageUrl: item.fallbackImageUrl ?? item.imageUrl })),
          itemsToRender: sc.itemsToRender?.map(item => ({ ...item, imageUrl: item.fallbackImageUrl ?? item.imageUrl })),
        })),
      }))
    );
    return () => { img.onerror = null }
  }, [menuCategories]);

  const findPromotionalItem = (menuCategories, promotionalProductId) => {
    for (const mc of menuCategories ?? []) {
      for (const sc of mc.subCategories ?? []) {
        const item = (sc.itemsToRender ?? []).find(item => item.id === promotionalProductId);
        if (item) {
          return { promoItem: item, isBundle: false };
        } else {
          const bundleItem = (sc.bundlesToRender ?? []).find(item => (item.id === promotionalProductId) || (item.name === promotionalProductId));
          if (bundleItem) {
            return { promoItem: bundleItem, isBundle: true };
          }
        }
      }
    }
    return { promoItem: null, isBundle: undefined };
  };

  return (
    <>
      {displayCategories?.map((mc, catIdx) => (

        <Fragment key={catIdx}>

          {catIdx === 0 && (() => {
            if (data) {
              const promoConfigSettings = {
                name: data.title,
                description: data.description,
                desktopImageUrl: data.desktopImage,
                mobileImageUrl: data.mobileImage,
                bgColor: data.backgroundColor,
                fontColor: data.fontColor,
                ctaFontColor: data.ctaFontColor,
                ctaBackgroundColor: data.ctaBackgroundColor,
                dietaryColor: data.dietaryColor,
                productId: data.productId
              }
              let { promoItem, isBundle } = findPromotionalItem(displayCategories, promoConfigSettings.productId);

              var promoStyle = !isSmallScreen ? { padding: '0 1rem' } : {};
              if (promoItem && isBundle !== undefined) {
                return (
                  <div key={`${mc.partitionKey}.subcategory${catIdx}.promotion`} style={promoStyle}>
                    <PromotionCardItem
                      idx={'promotion-' + promoConfigSettings.productId}
                      item={{ ...promoItem, cost: promoItem.displayPrice }}
                      bundleItem={isBundle}
                      addItem={addItem}
                      key={promoItem.id}
                      settings={promoConfigSettings}
                      navigateTo={navigateTo}
                      refs={refs}
                      showMenuHeaderAtTopOfPage={showMenuHeaderAtTopOfPage}
                      inApp={inApp}
                    />
                  </div>
                );
              }
              else {
                promoItem = {
                  item: {
                    id: 'promotion-0',
                    name: promoConfigSettings.name,
                    description: promoConfigSettings.description,
                    imageUrl: ''
                  }
                }
                return (
                  <div key={`${mc.partitionKey}.subcategory${catIdx}.promotion`} style={promoStyle}>
                    <PromotionCardItem
                      id={'promotion-' + 0}
                      index={0}
                      catIndex={catIdx}
                      key={promoItem?.name}
                      item={promoItem.item}
                      settings={promoConfigSettings}
                      navigateTo={navigateTo}
                      refs={refs}
                      showMenuHeaderAtTopOfPage={showMenuHeaderAtTopOfPage}
                      inApp={inApp}
                    />
                  </div>
                );
              }
            }
          })()}
          <section
            key={`${mc.name}.${catIdx}`}
            id={`category-${mc.name.replace(/\s+/g, '-')}`}
            ref={(cat) => {
              categoryRefs.current[catIdx] = cat;
            }}
            className='is-flex-wrap-wrap'
          >
            {mc.subCategories?.length > 0 && mc.subCategories.map((sc, subcatIdx) => (
              <Fragment key={`${mc.partitionKey}.subcategory${catIdx}.${subcatIdx}`}>
                <div
                  ref={(subCat) => {
                    if (!subCategoryRefs.current[catIdx]) {
                      subCategoryRefs.current[catIdx] = [];
                    }
                    subCategoryRefs.current[catIdx][subcatIdx] = subCat;
                  }}
                  style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <ItemMenuHeader
                    category={mc}
                    subcategory={sc}
                    categoryIdx={catIdx}
                    subcatIdx={subcatIdx}
                    subCategoryHeaderRefs={subCategoryHeaderRefs}
                    navigateTo={navigateTo}
                    hasPromotionItem={hasPromotionalCard.current}
                  />
                  <div data-testid={`sub-category-${mc.subCategories?.length === 1 && subcatIdx === 0 ? mc.name : sc.name}`} className='sub-category-item-wrapper'>
                    {(sc.bundlesToRender?.length === 0 && sc.itemsToRender?.length === 0) && (
                      <>
                        <div
                          key={`${catIdx}.${subcatIdx}`}
                          className='subcategory-no-items-card'
                        >
                          No results found. Please adjust your filters.
                        </div>
                      </>)}
                    {([...(sc.bundlesToRender ?? [])])
                      .sort(bundleDisplayIdxComparer)
                      .map((el, idx) => {

                        const inStock = isBundleInStock(el, []);
                        let itemBasketIndex = -1;
                        if (basket && basket.bundles) {
                          itemBasketIndex = basket.bundles.findIndex(bItem => bItem.name === el.name);
                        }
                        return (
                          <MenuItem
                            item={el}
                            basket={basket}
                            navigateTo={navigateTo}
                            bundleItem
                            menuItem
                            columnWidth={4}
                            key={el.name}
                            outOfStock={!inStock}
                            numberInBasket={itemBasketIndex === -1 ? 0 : findItemQuantity(el.name, true)}
                            idx={idx}
                            setQuickAddModalState={setQuickAddModalState}
                            quickAddModalState={quickAddModalState}
                            setMakeItAMealModalState={setMakeItAMealModalState}
                            makeItAMealModalState={makeItAMealModalState}
                            makeItAMealBundles={makeItAMealBundles}
                            makeItAMealBundleOptions={makeItAMealBundleOptions}
                            makeItAMealVariant={makeItAMealVariant}
                            makeItAMealModalTimesShown={makeItAMealModalTimesShown}
                            offer={menuOffers?.find(offer => offer.productIds?.includes(el.id)) ?? null}
                            onOfferSelected={onOfferSelected}
                          />
                        );
                      })
                    }
                    {([...sc.itemsToRender] ?? [])
                      .sort((a, b) => (a.outOfStock === b.outOfStock) ? 0 : (a.outOfStock ? 1 : -1))
                      .map((item, ind) => {
                        let itemBasketIndex = -1;
                        if (basket && basket.items) {
                          itemBasketIndex = basket.items.findIndex(bItem => bItem.id === item.id && bItem.name === item.name);
                        }
                        return (
                          <Fragment key={`${mc.partitionKey}.subcategory${catIdx}.${subcatIdx}.pizza-${item.id}-${ind}`}>
                            <MenuItem
                              id={'pizza-' + ind}
                              index={ind}
                              catIndex={catIdx}
                              item={{ ...item, cost: item.displayPrice }}
                              outOfStock={item.outOfStock}
                              columnWidth={4}
                              menuItem={true}
                              bundleItem={false}
                              numberInBasket={itemBasketIndex === -1 ? 0 : findItemQuantity(item.id, false, item.name)}
                              addItem={addItem}
                              removeItem={removeItem}
                              navigateTo={navigateTo}
                              dietaryOptions={item.dietPreferences}
                              isLast={ind === sc.subProducts.length - 1}
                              setQuickAddModalState={setQuickAddModalState}
                              quickAddModalState={quickAddModalState}
                              setMakeItAMealModalState={setMakeItAMealModalState}
                              makeItAMealModalState={makeItAMealModalState}
                              makeItAMealBundles={makeItAMealBundles}
                              makeItAMealBundleOptions={makeItAMealBundleOptions}
                              makeItAMealVariant={makeItAMealVariant}
                              makeItAMealModalTimesShown={makeItAMealModalTimesShown}
                              offer={menuOffers?.find(offer => offer.productIds?.includes(item.id)) ?? null}
                              onOfferSelected={onOfferSelected}
                            />
                          </Fragment>
                        );
                      })
                    }
                  </div>
                </div >
              </Fragment >
            ))}
          </section>
        </Fragment>
      ))}
    </>
  );
};

export default ItemMenu;
